import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable, SubscriptionLike as ISubscription } from 'rxjs';
import { filter, switchMap, tap } from 'rxjs/operators';

const HIDDEN_STATE = 'HIDDEN_STATE';
const LOADING_STATE = 'LOADING_STATE';
const EMPTY_STATE = 'EMPTY_STATE';
const SUGGESTIONS_STATE = 'SUGGESTIONS_STATE';

export { HIDDEN_STATE, LOADING_STATE, EMPTY_STATE, SUGGESTIONS_STATE };

export type Suggestion = {
  id: string;
  value: string;
};

export type TypeaheadSuggestionsState<S = Suggestion> = {
  greedy: boolean;
  highlighted: string | null;
  loading: boolean;
  name: 'HIDDEN_STATE' | 'LOADING_STATE' | 'EMPTY_STATE' | 'SUGGESTIONS_STATE';
  selected: S | null;
  suggestions: S[];
  value: string | null;
  allSuggestionsExhausted: boolean;
};

@Injectable({ providedIn: 'root' })
export class TypeaheadService {
  private static INITIAL_STATE: TypeaheadSuggestionsState = {
    name: HIDDEN_STATE,
    loading: false,
    value: null,
    selected: null,
    highlighted: null,
    suggestions: [],
    greedy: false,
    allSuggestionsExhausted: false,
  };

  private state$: BehaviorSubject<TypeaheadSuggestionsState> = new BehaviorSubject(
    TypeaheadService.INITIAL_STATE,
  );
  private innerStreamSub: ISubscription | null;

  public set greedy(val: boolean) {
    this.setState({
      greedy: val,
    });
  }

  public updateSuggestions(data: Suggestion[], allSuggestionsExhausted: boolean = false): void {
    const currentState = this.state$.getValue();
    this.setState(
      this.produceState(
        currentState.value!,
        currentState.name !== HIDDEN_STATE,
        data,
        allSuggestionsExhausted,
      ),
    );
  }

  public setup({
    initial,
    streams,
  }: {
    initial: { value: string; active: boolean };
    streams: { value$: Observable<string>; active$: Observable<boolean> };
  }): Observable<TypeaheadSuggestionsState> {
    this.setState(this.produceState(initial.value, false, false, false));

    this.innerStreamSub = streams.active$
      .pipe(
        tap(isActive => {
          const state = this.state$.getValue();
          if (isActive) {
            if (state.name === HIDDEN_STATE) {
              this.setState(
                this.produceState(state.value!, true, false, state.allSuggestionsExhausted),
              );
            }
          } else {
            this.setState({
              name: HIDDEN_STATE,
              loading: false,
            });
          }
        }),
        filter((isActive: boolean) => isActive),
        switchMap(() => streams.value$),
      )
      .subscribe(value =>
        this.setState(
          this.produceState(value, true, false, this.state$.getValue().allSuggestionsExhausted),
        ),
      );

    return this.data$;
  }

  public cleanup(): void {
    this.innerStreamSub!.unsubscribe();
    this.innerStreamSub = null;
  }

  public select(selected: Suggestion): void {
    this.setState({
      name: HIDDEN_STATE,
      suggestions: [],
      highlighted: null,
      value: selected.value,
      selected,
    });
  }

  public highlightNext(): void {
    const { highlighted, suggestions } = this.state$.getValue();

    if (suggestions.length) {
      if (!highlighted) {
        this.setState({
          highlighted: suggestions[0].id,
        });
      } else {
        const index = suggestions.indexOf(
          suggestions.find(suggestion => suggestion.id === highlighted)!,
        );
        if (index > -1 && index < suggestions.length - 1) {
          this.setState({
            highlighted: suggestions[index + 1].id,
          });
        }
      }
    }
  }

  public highlightPrev(): void {
    const { highlighted, suggestions } = this.state$.getValue();

    if (suggestions.length) {
      if (!highlighted) {
        this.setState({
          highlighted: suggestions[suggestions.length - 1].id,
        });
      } else {
        const index = suggestions.indexOf(
          suggestions.find(suggestion => suggestion.id === highlighted)!,
        );
        if (index > 0) {
          this.setState({
            highlighted: suggestions[index - 1].id,
          });
        }
      }
    }
  }

  public selectHighlighted(): Suggestion {
    const { highlighted, suggestions } = this.state$.getValue();
    const suggestion = suggestions.find(sug => sug.id === highlighted);

    if (suggestion) {
      this.select(suggestion);
    }

    return suggestion!;
  }

  public setState(state: Partial<TypeaheadSuggestionsState>): void {
    const oldState = this.state$.getValue();
    const newState = { ...oldState, ...state };
    this.state$.next(newState);
  }

  public get state(): TypeaheadSuggestionsState {
    return this.state$.getValue();
  }

  public get data$(): Observable<TypeaheadSuggestionsState> {
    return this.state$.asObservable();
  }

  // eslint-disable-next-line max-lines-per-function
  private produceState(
    value: string | null = null,
    isActive: boolean = false,
    newSuggestions: Suggestion[] | boolean,
    allSuggestionsExhausted: boolean,
  ): TypeaheadSuggestionsState {
    const oldState = this.state$.getValue();
    const newState: any = {};
    const _newSuggestions = newSuggestions || oldState.suggestions;

    newState.allSuggestionsExhausted = allSuggestionsExhausted;

    if (isActive) {
      newState.value = value;

      if (value !== null && value !== oldState.value) {
        newState.name = LOADING_STATE;
        newState.loading = true;
        newState.selected = null;
      } else if (
        !oldState.selected ||
        oldState.selected.value !== value ||
        oldState.value === newState.value
      ) {
        newState.name =
          Array.isArray(_newSuggestions) && _newSuggestions.length > 0
            ? SUGGESTIONS_STATE
            : EMPTY_STATE;
        newState.loading = false;
        newState.suggestions = _newSuggestions;
      } else {
        newState.name = HIDDEN_STATE;
        newState.loading = false;
        newState.suggestions = [];
      }

      if (oldState.greedy && newState.suggestions && newState.suggestions.length) {
        newState.highlighted = newState.suggestions[0].id;
      } else {
        newState.highlighted = null;
      }
    }

    return newState;
  }
}
